import { FormProvider } from "react-hook-form";
import MediaLibrarySearchBox from "./MediaLibrarySearchBox/MediaLibrarySearchBox";
import MediaList from "./MediaList/MediaList";
import Modal from "~/common/components/Modal";
import { Button, CircularProgress, Portal, Stack, Typography, Box } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import MediaPreview from "./MediaPreview/MediaPreview";
import type { MediaFilterData, MediaFormFields } from "../types/mediaLibraryFormTypes";
import { useMediaLibrary } from "./useMediaLibrary";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useCallback } from "react";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

interface Props {
  data: MediaFilterData;
}

const MedaiLibraryPage = ({ data }: Props) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const isPhone = screenSize <= useNumericScreenSize.phone;

  const {
    formProps,
    mediaItems,
    selectedMediaItem,
    isOpen,
    loading,
    error,
    stableFilterOptions,
    errorSnackbarOpen,
    loginPopupOpen,
    isDownloading,
    handleModalClose,
    handleCardClick,
    fetchMedia,
    setErrorSnackbarOpen,
    handleFavouriteClick,
    updateMediaItems,
    updateSelectedMediaItem,
    handleDownload,
    openLoginPopup,
    closeLoginPopup,
    clearFavFromSessionStorage,
  } = useMediaLibrary(data);
  const { val: resetPopupOpen, setTrue: openResetPopup, setFalse: closeResetPopup } = useBooleanState(false);

  const onFormSubmit = useCallback(
    (formValues: MediaFormFields) => {
      return fetchMedia({ ...formValues, page: 0 }, true);
    },
    [fetchMedia],
  );

  return (
    <FormProvider {...formProps}>
      <Box>
        <Typography variant="headlineLarge" fontWeight="bold" color="text.primary">
          {t("media.library.pageTitle")}
        </Typography>
        <MediaLibrarySearchBox filterOptions={stableFilterOptions} submitForm={onFormSubmit} />
        {error ? (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        ) : (
          <MediaList
            mediaItems={mediaItems}
            loading={loading}
            handleCardClick={handleCardClick}
            handleFavouriteClick={handleFavouriteClick}
            handleDownload={handleDownload}
            isDownloading={isDownloading}
          />
        )}
      </Box>
      {isOpen && selectedMediaItem && (
        <Modal
          title={t("media.library.preview.title")}
          onClose={handleModalClose}
          footerChildren={
            <Stack direction="row" gap={1} width={isPhone ? 1 : "auto"}>
              <Button variant="text" sx={{ flex: 1 }} onClick={handleModalClose}>
                {t("close")}
              </Button>
              <Button
                sx={{ flex: 1 }}
                disabled={isDownloading}
                onClick={() => {
                  handleDownload(selectedMediaItem);
                }}
              >
                {t("media.library.preview.download")}
              </Button>
            </Stack>
          }
          footerClassName={isPhone ? "scha__modal__footer--border" : ""}
        >
          <MediaPreview
            id={selectedMediaItem.id}
            groupId={selectedMediaItem.groupId}
            updateMediaItems={updateMediaItems}
            updateSelectedMediaItem={updateSelectedMediaItem}
            openLoginPopup={openLoginPopup}
          />
        </Modal>
      )}
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={() => {
            openResetPopup();
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
          close={() => {
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup close={closeResetPopup} initialEmail={""} />}
      <Portal>
        <Snackbar
          open={errorSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setErrorSnackbarOpen(false)}
          action={<SnackbarCloseButton onClick={() => setErrorSnackbarOpen(false)} />}
          message={t("media.library.dowloadFailed")}
        />
        <Snackbar
          open={isDownloading}
          action={<CircularProgress size={20} color={"inherit"} />}
          message={t("media.library.file.downloading")}
        />
      </Portal>
    </FormProvider>
  );
};

export function createMediaLibraryPage(data: MediaFilterData) {
  return <MedaiLibraryPage data={data} />;
}
